import { PawPrint } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import {
  BlogLinks,
  MenuLink,
  PageLinks,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { cn } from '@/lib/cn';

/**
 * Pawly's footer grid, by how many link columns stand beside the wordmark.
 * Written out because Tailwind reads class names out of the source and never
 * sees a string built at runtime.
 */
const PAWLY_FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[2fr_1fr]',
  2: 'lg:grid-cols-[2fr_1fr_1fr]',
  3: 'lg:grid-cols-[2fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[2fr_1fr_1fr_1fr_1fr]',
};

/** One footer column: a tall capital heading over its links. */
function PawlyColumn({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-4 text-xl uppercase leading-none tracking-[0.01em]"
        style={{ fontFamily: 'var(--storefront-heading-font)', color: 'var(--storefront-text)' }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The close of a cream page: an oat band whose top corners take the theme's
 * radius, so the footer reads as one more rounded tile laid under the page.
 * The wordmark with its tangerine paw and the shop's line on the left, then
 * the footer menu, the shelves and the pages beside it - each skipped when
 * empty and the grid sized to what is left - then the fine print under a
 * hairline.
 *
 * Dark type on the oat throughout: the band is the merchant's surface colour,
 * which a merchant keeps pale, so the copy stays in their text colour rather
 * than a fixed light one.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('header.menu_categories')
      : shopTitle;

  return (
    <footer
      className="mt-10 pt-16 text-[14px]"
      style={{
        backgroundColor: 'var(--storefront-surface)',
        color: 'var(--storefront-muted)',
        borderTopLeftRadius: 'calc(var(--storefront-radius) * 1.5)',
        borderTopRightRadius: 'calc(var(--storefront-radius) * 1.5)',
      }}
    >
      <StorefrontContainer
        className={cn('grid gap-10 pb-14 text-start sm:grid-cols-2', PAWLY_FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="flex items-center gap-1.5 text-4xl uppercase leading-none tracking-[0.01em]"
            style={{ fontFamily: 'var(--storefront-heading-font)', color: 'var(--storefront-text)' }}
          >
            {storeName}
            <PawPrint
              aria-hidden
              className="size-6"
              style={{ color: 'var(--storefront-accent)' }}
              fill="currentColor"
              strokeWidth={0}
            />
          </p>
          {description ? <p className="mt-5 max-w-[24rem] leading-relaxed">{description}</p> : null}
        </div>
        {items.length > 0 ? (
          <PawlyColumn title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-2.5">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="hover:text-[color:var(--storefront-text)] hover:underline"
                />
              ))}
            </nav>
          </PawlyColumn>
        ) : null}
        {shelfColumns.map((column, index) => (
          <PawlyColumn key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-2.5"
            >
              {column.map((category) => (
                <Link
                  key={category.id}
                  to={categoryPath(category.slug)}
                  className="hover:text-[color:var(--storefront-text)] hover:underline"
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </PawlyColumn>
        ))}
        {preview ? null : (
          <PawlyColumn title={copy('theme.footer_support')}>
            <PageLinks className="mb-2.5 flex-col items-start justify-start gap-2.5" />
            <BlogLinks className="flex-col items-start justify-start gap-2.5" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </PawlyColumn>
        )}
      </StorefrontContainer>
      <StorefrontContainer>
        <div
          className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t py-6 text-[12px]"
          style={{ borderColor: 'color-mix(in srgb, var(--storefront-text) 15%, transparent)' }}
        >
          <LanguageSwitcherSlot placement="footer" preview={preview} />
          <span style={copyrightStyle(footer)}>
            © {new Date().getFullYear()} {storeName} ·{' '}
            {copy('theme.powered_by', { brand: 'NeonCarts' })}
          </span>
        </div>
      </StorefrontContainer>
    </footer>
  );
};
